/**
 * ProjectileSim — the client-side projectile *simulation*: which projectiles
 * exist right now and where they are. Pure functions over gameState; no PIXI,
 * no DOM. The renderer only draws what this produces.
 *
 * Before Phase 3D this logic lived in PixiEntityRenderer (extrapolation and
 * projectile-vs-player hit tests inside the draw loop) and in client.js
 * (predicted-projectile pruning), so it could not be unit-tested and the
 * renderer decided gameplay-looking facts ("this bullet hit someone").
 *
 * Two kinds of projectile are tracked:
 *   - server projectiles: gameState.projectiles (+ gameState.serverProjectileState
 *     with receiveTime/x/y/vx/vy/terminationTime) — authoritative, extrapolated
 *     from the last snapshot;
 *   - predicted projectiles: gameState.predictedProjectiles — spawned locally on
 *     fire so the shooter sees an instant tracer; removed once the server's copy
 *     has been shown for TRANSITION_MS, or when they expire / leave the map.
 */

import { projectileHitRadius } from '../../shared/ShotModel.js';

export const TRANSITION_MS = 50;       // keep a confirmed predicted projectile this long before dropping it
export const PLAYER_HIT_RADIUS = projectileHitRadius(4); // default-size projectile

/** Position of a projectile at time t (ms), from its origin and velocity (px/s). */
export function positionAt(origin, tMs) {
  const dt = (tMs - origin.t0) / 1000;
  return { x: origin.x0 + origin.vx * dt, y: origin.y0 + origin.vy * dt };
}

/**
 * Does the projectile at (x, y) overlap any live player other than its owner?
 * Players are sampled at their *rendered* time (now − interpolationDelay) so
 * the client-side hit matches what the shooter sees.
 */
export function hitsPlayer(x, y, ownerId, players, nowMs, interpolationDelay, radius = PLAYER_HIT_RADIUS) {
  const r2 = radius * radius;
  for (const [playerId, player] of players) {
    if (playerId === ownerId || player.isDead) continue;
    const s = typeof player.getRenderState === 'function' ? player.getRenderState(nowMs - interpolationDelay) : player;
    const dx = x - s.x;
    const dy = y - s.y;
    if (dx * dx + dy * dy < r2) return true;
  }
  return false;
}

/**
 * Remove predicted projectiles that are confirmed-and-transitioned, expired,
 * or off the map. Mutates gameState.predictedProjectiles in place (the array
 * identity is shared with client.js).
 */
export function prunePredicted(gameState, nowMs, { mapWidth, mapHeight }) {
  const list = gameState.predictedProjectiles;
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i];
    if (p.serverConfirmed && nowMs - p.serverConfirmedAt > TRANSITION_MS) { list.splice(i, 1); continue; }
    const dt = (nowMs - p.createdAt) / 1000;
    const x = p.initialX + p.vx * dt;
    const y = p.initialY + p.vy * dt;
    if (nowMs - p.createdAt > p.projectileLifetime || x < 0 || x > mapWidth || y < 0 || y > mapHeight) {
      list.splice(i, 1);
    }
  }
}

/**
 * Build the list of projectiles the renderer should draw this tick.
 * Each entry carries an `origin` so the renderer can extrapolate to the exact
 * frame time without re-deciding anything.
 *
 * @returns {Array<{ key: string, projectile: object, origin: { x0, y0, vx, vy, t0 } }>}
 */
export function visibleProjectiles(gameState, nowMs, { interpolationDelay }) {
  const out = [];
  const predictedTwin = new Set(gameState.predictedProjectiles.map((p) => p.serverId).filter(Boolean));

  for (const projectile of gameState.projectiles) {
    if (predictedTwin.has(projectile.id)) continue; // the predicted copy is still on screen
    const state = gameState.serverProjectileState.get(projectile.id);
    if (!state) continue;
    if (nowMs - state.receiveTime >= state.terminationTime) continue;
    const origin = { x0: state.x, y0: state.y, vx: state.vx, vy: state.vy, t0: state.receiveTime };
    const { x, y } = positionAt(origin, nowMs);
    const radius = projectileHitRadius(projectile.projectileSize);
    if (hitsPlayer(x, y, projectile.ownerId, gameState.players, nowMs, interpolationDelay, radius)) continue;
    out.push({ key: `server_${projectile.id}`, projectile, origin });
  }

  for (const projectile of gameState.predictedProjectiles) {
    if (nowMs - projectile.createdAt >= projectile.terminationTime) continue;
    const origin = { x0: projectile.initialX, y0: projectile.initialY, vx: projectile.vx, vy: projectile.vy, t0: projectile.createdAt };
    const { x, y } = positionAt(origin, nowMs);
    const radius = projectileHitRadius(projectile.projectileSize);
    const hit = firstHit(x, y, projectile.ownerId, gameState.players, nowMs, interpolationDelay, radius, projectile.ownerTeam ?? null);
    if (hit) {
      // First contact on the shooter's screen: stop the tracer here and spark (spec D8).
      // The server still decides the damage; its hitmarker follows ~RTT later.
      projectile.terminationTime = nowMs - projectile.createdAt;
      if (!Array.isArray(gameState.predictedImpacts)) gameState.predictedImpacts = [];
      gameState.predictedImpacts.push({ x, y, targetId: hit.playerId, targetX: hit.x, targetY: hit.y, radius, startTime: nowMs });
      continue;
    }
    out.push({ key: `pred_${projectile.id}`, projectile, origin });
  }

  return out;
}

/**
 * One simulation step: prune predicted projectiles, then publish the visible
 * set on gameState.visibleProjectiles for the renderer.
 */
export function stepProjectiles(gameState, nowMs, params) {
  prunePredicted(gameState, nowMs, params);
  gameState.visibleProjectiles = visibleProjectiles(gameState, nowMs, params);
  return gameState.visibleProjectiles;
}

/**
 * Where a server projectile's flight starts, from a projectileSpawned payload.
 * The server reports the muzzle (originX/Y) and how long the projectile had
 * already flown when announced (ageMs, lag-compensation catch-up); x/y is where
 * it is now — or, when `resolved`, where it hit. Older payloads carry only x/y.
 * @returns {{ x:number, y:number, t0:number, terminationTime:number|null, streak:{x0:number,y0:number,x1:number,y1:number}|null }}
 */
export function spawnStateFor(proj, receiveTime) {
  const hasOrigin = Number.isFinite(proj.originX) && Number.isFinite(proj.originY);
  const ageMs = hasOrigin && proj.ageMs > 0 ? proj.ageMs : 0;
  const x = hasOrigin ? proj.originX : proj.x;
  const y = hasOrigin ? proj.originY : proj.y;
  return {
    x,
    y,
    t0: receiveTime - ageMs,
    terminationTime: proj.resolved ? ageMs : null,
    streak: ageMs > 0 ? { x0: x, y0: y, x1: proj.x, y1: proj.y } : null
  };
}
/**
 * The first live enemy whose rendered circle contains (x, y), or null. Players
 * are sampled at their rendered time, like hitsPlayer; teammates are skipped
 * (friendly fire passes through). Returns where the target was drawn.
 */
export function firstHit(x, y, ownerId, players, nowMs, interpolationDelay, radius, ownerTeam = null) {
  const r2 = radius * radius;
  for (const [playerId, player] of players) {
    if (playerId === ownerId || player.isDead) continue;
    if (ownerTeam !== null && ownerTeam !== undefined && player.team === ownerTeam) continue;
    const s = typeof player.getRenderState === 'function' ? player.getRenderState(nowMs - interpolationDelay) : player;
    const dx = x - s.x;
    const dy = y - s.y;
    if (dx * dx + dy * dy < r2) return { playerId, x: s.x, y: s.y };
  }
  return null;
}
